import React, { useContext, useEffect, useState } from "react";
import { css } from "@emotion/css";
import { PageLayout } from "components";
import { useAuth } from "hooks";
import ReactGA from "react-ga4";
import AliceCarousel, { DotsItem } from "react-alice-carousel";
import "react-alice-carousel/lib/alice-carousel.css";
import {
  AvgEmailsPerDay,
  BusiessDay,
  StatsStart,
  TimeSpendInEmail,
  TimeSpendInEmailRef,
  TotalEmailReceived,
  EmailRepartition,
  EmailThatNeededAttention,
  AvgResponseTime,
  StatsSummary,
} from "./components";
import { CARD_HEIGHT } from "utils/constants";
import { htmlToImage, recordActivity } from "utils/helpers";
import download from "downloadjs";
import { UserContext } from "context";

export const HomePage: React.FC = () => {
  useAuth();
  const totalEmailRef = React.useRef<HTMLDivElement>(null);
  const avgEmailRef = React.useRef<HTMLDivElement>(null);
  const timeSpendInEmailRef = React.useRef<HTMLDivElement>(null);
  const timeSpendInEmailRefRef = React.useRef<HTMLDivElement>(null);
  const businessDayRef = React.useRef<HTMLDivElement>(null);
  const emailRepartitionRef = React.useRef<HTMLDivElement>(null);
  const emailThatNeededAttentionRef = React.useRef<HTMLDivElement>(null);
  const avgResponseTimeRef = React.useRef<HTMLDivElement>(null);
  const statSummaryRef = React.useRef<HTMLDivElement>(null);
  const carouselRef = React.useRef<AliceCarousel>(null);

  const [totalEmailShareLoading, setTotalEmailShareLoading] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);

  const { state: userState } = useContext(UserContext);

  const handleShare = async (
    _option: "twitter" | "facebook" | "linkedin",
    stats: string,
  ) => {
    let ref = null;
    switch (stats) {
      case "totalEmail":
        ref = totalEmailRef;
        break;
      case "avgEmail":
        ref = avgEmailRef;
        break;
      case "timeSpendInEmail":
        ref = timeSpendInEmailRef;
        break;
      case "timeSpendInEmailRef":
        ref = timeSpendInEmailRefRef;
        break;
      case "businessDay":
        ref = businessDayRef;
        break;
      case "emailRepartition":
        ref = emailRepartitionRef;
        break;
      case "emailThatNeededAttention":
        ref = emailThatNeededAttentionRef;
        break;
      case "avgResponseTime":
        ref = avgResponseTimeRef;
        break;
      case "statSummary":
        ref = statSummaryRef;
        break;
    }
    setTotalEmailShareLoading(true);
    ReactGA.event({
      category: "Home",
      action: "Share",
    });
    if (userState?._id) {
      recordActivity({
        data: {
          user: userState._id,
          event: "wrapped-stats-shared",
          source: "stats",
          data: {
            stats,
          },
        },
      })
        .then()
        .catch();
    }

    if (!ref) {
      setTotalEmailShareLoading(false);
      return;
    }
    // set the css display to flex
    ref!.current!.style.display = "flex";
    const png = await htmlToImage(ref?.current as HTMLElement);
    if (png) {
      download(png, "email-stats.png");
      setTotalEmailShareLoading(false);
      return;
    }
  };

  const handleStart = () => {
    carouselRef.current?.slideNext();
    setAutoPlay(true);
    if (userState?._id) {
      recordActivity({
        data: {
          user: userState._id,
          event: "wrapped-stats-started",
          source: "stats",
          data: {},
        },
      })
        .then()
        .catch();
    }
  };

  useEffect(() => {
    const handleKeyPress = (ev: KeyboardEvent) => {
      ev.preventDefault();
      if (ev.key === "ArrowRight") {
        handleNext();
      } else if (ev.key === "ArrowLeft") {
        handlePrev();
      }
    };
    const handleNext = () => {
      carouselRef.current?.slideNext();
    };

    const handlePrev = () => {
      carouselRef.current?.slidePrev();
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  const items = [
    <StatsStart index={1} handleNext={handleStart} />,
    <TotalEmailReceived
      index={2}
      ref={totalEmailRef}
      handleShare={(option) => handleShare(option, "totalEmail")}
      shareLoading={totalEmailShareLoading}
    />,
    <AvgEmailsPerDay
      index={3}
      ref={avgEmailRef}
      handleShare={(option) => handleShare(option, "avgEmail")}
      shareLoading={totalEmailShareLoading}
    />,
    <TimeSpendInEmail
      index={4}
      ref={timeSpendInEmailRef}
      handleShare={(option) => handleShare(option, "timeSpendInEmail")}
      shareLoading={totalEmailShareLoading}
    />,
    <TimeSpendInEmailRef
      index={5}
      ref={timeSpendInEmailRefRef}
      handleShare={(option) => handleShare(option, "timeSpendInEmailRef")}
      shareLoading={totalEmailShareLoading}
    />,
    <BusiessDay
      index={6}
      ref={businessDayRef}
      handleShare={(option) => handleShare(option, "businessDay")}
      shareLoading={totalEmailShareLoading}
    />,
    <EmailRepartition
      index={7}
      ref={emailRepartitionRef}
      handleShare={(option) => handleShare(option, "emailRepartition")}
      shareLoading={totalEmailShareLoading}
    />,
    <EmailThatNeededAttention
      index={8}
      ref={emailThatNeededAttentionRef}
      handleShare={(option) => handleShare(option, "emailRepartition")}
      shareLoading={totalEmailShareLoading}
    />,
    <AvgResponseTime
      index={9}
      ref={avgResponseTimeRef}
      handleShare={(option) => handleShare(option, "avgResponseTime")}
      shareLoading={totalEmailShareLoading}
    />,
    <StatsSummary
      index={10}
      ref={statSummaryRef}
      handleShare={(option) => handleShare(option, "statSummary")}
      shareLoading={totalEmailShareLoading}
    />,
    //<StatsEnd index={11} />,
  ];

  return (
    <PageLayout title="Home | Wrapped">
      <div
        className={css`
          max-width: 462px;
          width: 100%;
          height: 100vh;
          margin: auto;
          display: flex;
          justify-content: center;
          align-items: center;
        `}
      >
        <div
          className={css`
            max-width: 562px;
            width: 100%;
            height: 100%;
            max-height: ${CARD_HEIGHT}px;
          `}
          onMouseEnter={() => setAutoPlay(false)}
          onMouseLeave={() => setAutoPlay(true)}
        >
          <AliceCarousel
            ref={carouselRef}
            // onSlideChanged={(e) => {}}
            mouseTracking
            autoPlay={autoPlay}
            autoPlayControls
            autoPlayStrategy="action"
            autoPlayInterval={4000}
            animationDuration={600}
            animationType="slide"
            infinite={false}
            touchTracking={false}
            disableButtonsControls
            items={items}
            renderDotsItem={renderDotsItem}
            // renderNextButton={(props) => renderNextItem({...props, activeIndex: carouselRef.current?.state?.activeIndex})}
            // renderPrevButton={renderPrevItem}
            renderPlayPauseButton={renderPlayPauseButton}
          />
        </div>
      </div>
    </PageLayout>
  );
};

function renderDotsItem({ isActive }: DotsItem) {
  return (
    <div
      className={css`
        height: 6px;
        width: ${isActive ? 79.5 : 12}px;
        border-radius: 3px;
        background-color: ${isActive ? "#8d86f8" : "#322D4E"};
        margin-left: 12px;
        cursor: pointer;
        transition: 1s ease-in-out width;
      `}
    />
  );
}

function renderPlayPauseButton() {
  return "";
}
